import React, { useState } from "react";
import PropTypes from "prop-types";
import { usePost } from "redux/fetch-hooks";

import { CardLevelOne } from "common/DS/ElevatedCard";
import { H4, Subtitle2 } from "common/DS/Text";
import TextField from "common/DS/TextField";
import QuestionButtonGroup from "common/Question/QuestionButtonGroup";
import { heapTrack } from "lib/analytics";

export const CompanyHubAuthCard = (
    {
        title,
        subtitle,
        field1,
        field2,
        companySlug,
        buttonRenamer,
        setAuthenticated,
        setFirstName,
        setSurveyList,
    },
    { t: _ },
) => {
    const field1Placeholder = _("Enter your {field1}", { field1 });
    const field1Label = field1;
    const field1Helper = _("Please enter a valid {field1}", { field1 });
    const field2Placeholder = _("Enter your {field2}", { field2 });
    const field2Label = field2;
    const field2Helper = _("Please enter a valid {field2}", { field2 });

    const [hasField1Error, setHasField1Error] = useState(false);
    const [hasField2Error, setHasField2Error] = useState(false);
    const [field1Input, setField1Input] = useState("");
    const [field2Input, setField2Input] = useState("");
    const [field1HelperText, setField1HelperText] = useState("");
    const [field2HelperText, setField2HelperText] = useState("");
    const [hasInput, setHasInput] = useState(false);
    const buttonClassName = "hub__login-buttons";

    function onField1Change(e) {
        const textEntered = e.target.value.trim();
        setHasInput(textEntered.length > 0 && !!field2Input);
        setField1Input(textEntered);
        setField1HelperText("");
        setHasField1Error(false);
    }

    function onField2Change(e) {
        const textEntered = e.target.value.trim();
        setHasInput(textEntered.length > 0 && !!field1Input);
        setField2Input(textEntered);
        setField2HelperText("");
        setHasField2Error(false);
    }

    function onResend() {
        setField1Input("");
        setField2Input("");
        setField1HelperText("");
        setField2HelperText("");
        setHasField1Error(false);
        setHasField2Error(false);
    }

    let failCount = 0;

    const [postAuthCode, { isLoading }] = usePost(`/check-in/auth/code/verify/${companySlug}`, {
        onSuccess: (data) => {
            heapTrack("Hub Login - redirecting to survey", { companySlug });
            setFirstName(data.firstName);
            setSurveyList(data.surveyList);
            setAuthenticated(true);
        },
        onError: (error) => {
            if (error?.json?.error) {
                setField2HelperText(error.json.error);
                setField1HelperText("");
                setHasField1Error(false);
                setHasField2Error(true);
                failCount += 1;

                if (failCount >= 3) {
                    window.location.replace("/hub/");
                }
            }
        },
    });

    function onField1Blur() {
        if (!field1Input.length) {
            setField1HelperText(field1Helper);
            setHasField1Error(true);
        }
    }

    function onField2Blur() {
        if (!field2Input.length) {
            setField2HelperText(field2Helper);
            setHasField2Error(true);
        }
    }

    function onNext() {
        setHasField1Error(false);
        setHasField2Error(false);
        setField1HelperText("");
        setField2HelperText("");

        postAuthCode({
            data: {
                csrf_token: window.csrfToken,
                field1: field1Input,
                field2: field2Input,
            },
        });
    }

    return (
        <CardLevelOne className="hub__login">
            <H4>{title}</H4>
            <Subtitle2 className="hub__login-subtitle">{subtitle}</Subtitle2>
            <TextField
                className="hub__login-text-box"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={field1Input}
                placeholder={field1Placeholder}
                onChange={(e) => onField1Change(e)}
                onBlur={(e) => onField1Blur(e)}
                label={field1Input ? field1Label : ""}
                helperText={_(field1HelperText)}
                error={hasField1Error}
                useValidators={false}
            />
            <TextField
                className="hub__login-text-box"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={field2Input}
                placeholder={field2Placeholder}
                onChange={(e) => onField2Change(e)}
                onBlur={(e) => onField2Blur(e)}
                label={field2Input ? field2Label : ""}
                helperText={_(field2HelperText)}
                error={hasField2Error}
                useValidators={false}
            />
            <QuestionButtonGroup
                handleSubmit={onNext}
                handleSkip={onResend}
                nextDisabled={!hasInput || isLoading}
                additionalClassNames={buttonClassName}
                buttonRenamer={buttonRenamer}
            />
        </CardLevelOne>
    );
};

CompanyHubAuthCard.contextTypes = {
    t: PropTypes.func,
};
CompanyHubAuthCard.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    field1: PropTypes.string.isRequired,
    field2: PropTypes.string.isRequired,
    companySlug: PropTypes.string.isRequired,
    buttonRenamer: PropTypes.object,
    setAuthenticated: PropTypes.func.isRequired,
    setFirstName: PropTypes.func.isRequired,
    setSurveyList: PropTypes.func.isRequired,
};
CompanyHubAuthCard.defaultProps = {
    subtitle: null,
    buttonRenamer: {},
};
